/**
 * ================================================================
 * API SERVICE: Download Center Service
 * ================================================================
 *
 * @service          downloadCenterApi
 * @description      API service for managing download center functionality
 * @baseUrl          reportsBaseUrl (shared with reports service)
 *
 * @endpoints
 * - GET    /get-company-codes - Get available reports for download center
 * - GET    /trading-holding-report - Download center holdings report
 * - GET    /ledger-report - Download center ledger report
 * - GET    /position-report - Download center position report
 * - GET    /contract-bill-report - Download center contract bill
 * - GET    /global-summary-report - Download center global summary
 * - GET    /income-tax-report - Download center income tax report
 * - GET    /fno-position-report - Download center F&O report
 * - GET    /aging-report - Download center aging report
 * - GET    /revenue-report - Download center revenue report
 * - GET    /portfolio-analysis-report - Download center portfolio analysis
 * - GET    /performance-report - Download center performance report
 * - GET    /compliance-report - Download center compliance report
 * - GET    /audit-trail-report - Download center audit trail
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit        100 requests per minute per user
 *
 * @caching
 * - Strategy: RTK Query automatic caching with tags
 * - Tags: ['DownloadCenter'] for cache invalidation
 * - TTL: 5 minutes for company codes, no caching for downloads
 *
 * @errorHandling
 * - Network errors: Automatic retry with exponential backoff
 * - Auth errors: Redirect to login page
 * - Validation errors: Display user-friendly messages
 *
 * @types
 * - All download center types imported from @/types/downloads
 * - Standardized error response format from @/types/common
 *
 * ================================================================
 */

import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { createApiErrorTransformer } from '@/lib/api/baseQuery';
import { prepareAuthHeaders } from '@/lib/session/sessionManager';
import type {
  ContractBillParams,
  CurrencyDerivativesParams,
  ClientMasterParams,
  DownloadCenterResponse,
  EquityReportParams,
  FnoReportParams,
  GetCompanyCodesResponse,
  GlobalDetailsParams,
  GlobalSummaryParams,
  HoldingsReportParams,
  IncomeTaxParams,
  LedgerReportParams,
  McxReportParams,
  MutualFundsReportParams,
  PayinPayoutParams,
  PnlSummaryParams,
  PositionReportParams,
  SlbmReportParams,
  ClientMasterResponse,
} from '@/types/downloads';

const reportsBaseUrl = import.meta.env.VITE_REPORTS_URL;
const clientMasterReports = import.meta.env.VITE_CONNECT_URL;

export const downloadCenterApi = createApi({
  reducerPath: 'downloadCenterApi',
  baseQuery: fetchBaseQuery({
    baseUrl: reportsBaseUrl,
    // ================================================================
    // BASE QUERY CONFIGURATION
    // ================================================================
    //
    // Authentication: Include AuthToken in headers via common utility
    // Error handling: Transform errors to standard format
    // Request/Response: JSON content type with proper serialization
    // Security: Centralized token management for consistency
    // ================================================================
    prepareHeaders: prepareAuthHeaders,
  }),
  tagTypes: ['DownloadCenter'],
  endpoints: (builder) => ({
    // ================================================================
    // DOWNLOAD CENTER CONFIGURATION ENDPOINTS
    // ================================================================

    /**
     * Get available company codes and reports for download center
     *
     * @endpoint GET /get-company-codes
     * @param {string} clientCode - Client code parameter
     * @returns {GetCompanyCodesResponse} Available reports and company codes
     * @cache Provides 'DownloadCenter' tag for invalidation
     */
    getCompanyCodes: builder.query<
      GetCompanyCodesResponse,
      { clientCode?: string; source?: string; financialYear?: string }
    >({
      query: (params) => ({
        url: 'get-company-codes',
        params,
      }),
      providesTags: ['DownloadCenter'],
      keepUnusedDataFor: 300, // 5 minutes cache
      transformErrorResponse: createApiErrorTransformer('DownloadCenterCompanyCodes', true),
    }),

    // ================================================================
    // DOWNLOAD CENTER REPORT ENDPOINTS
    // ================================================================

    /**
     * Download holdings report for download center
     *
     * @endpoint GET /trading-holding-report
     * @param {HoldingsReportParams} params - Holdings report parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadHoldingsReportCenter: builder.query<DownloadCenterResponse, HoldingsReportParams>({
      query: (params) => ({
        url: 'dp-holding-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterHoldings', true),
    }),

    /**
     * Download ledger report for download center
     *
     * @endpoint GET /ledger-report
     * @param {LedgerReportParams} params - Ledger report parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadLedgerReportForCenter: builder.query<DownloadCenterResponse, LedgerReportParams>({
      query: (params) => ({
        url: 'ledger-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterLedger', true),
    }),

    /**
     * Download position report for download center
     *
     * @endpoint GET /position-report
     * @param {PositionReportParams} params - Position report parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadPositionReportForCenter: builder.query<DownloadCenterResponse, PositionReportParams>({
      query: (params) => ({
        url: 'position-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterPosition', true),
    }),

    /**
     * Download contract bill report for download center
     *
     * @endpoint GET /contract-bill-report
     * @param {ContractBillParams} params - Contract bill parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadContractBillReport: builder.query<DownloadCenterResponse, ContractBillParams>({
      query: (params) => ({
        url: 'contract-bill-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterContractBill', true),
    }),

    /**
     * Download global summary for download center
     *
     * @endpoint GET /global-summary-report
     * @param {GlobalSummaryParams} params - Global summary parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadGlobalSummaryForCenter: builder.query<DownloadCenterResponse, GlobalSummaryParams>({
      query: (params) => ({
        url: 'global-summary-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterGlobalSummary', true),
    }),

    /**
     * Download income tax report for download center
     *
     * @endpoint GET /income-tax-report
     * @param {IncomeTaxParams} params - Income tax parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadIncomeTaxReport: builder.query<DownloadCenterResponse, IncomeTaxParams>({
      query: (params) => ({
        url: 'consolidated-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterIncomeTax', true),
    }),

    /**
     * Download F&O position report for download center
     *
     * @endpoint GET /fno-position-report
     * @param {FnoReportParams} params - F&O report parameters
     * @returns {DownloadCenterResponse} Base64 encoded file data
     * @cache No caching for downloads
     */
    downloadFnoReport: builder.query<DownloadCenterResponse, FnoReportParams>({
      query: (params) => ({
        url: 'fno-report',
        params,
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterFNO', true),
    }),

    // ================================================================
    // ADDITIONAL REPORTS (NEW)
    // ================================================================

    /**
     * Download P&L Summary report
     * @endpoint GET /pnl-report
     */
    downloadPnlSummaryReport: builder.query<DownloadCenterResponse, PnlSummaryParams>({
      query: (params) => ({ url: 'pnl-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterPNL', true),
    }),

    /**
     * Download Global Details report
     * @endpoint GET /global-details-report
     */
    downloadGlobalDetailsReport: builder.query<DownloadCenterResponse, GlobalDetailsParams>({
      query: (params) => ({ url: 'global-details-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterGlobalDetails', true),
    }),

    /**
     * Download Payin Payout report
     * @endpoint GET /payin-payout-report
     */
    downloadPayinPayoutReport: builder.query<DownloadCenterResponse, PayinPayoutParams>({
      query: (params) => ({ url: 'payin-out-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterPayinPayout', true),
    }),

    /**
     * Download Currency Derivatives report
     * @endpoint GET /currency-derivatives-report
     */
    downloadCurrencyDerivativesReport: builder.query<
      DownloadCenterResponse,
      CurrencyDerivativesParams
    >({
      query: (params) => ({ url: 'cd-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterCurrencyDerivatives', true),
    }),

    /**
     * Download MCX report
     * @endpoint GET /mcx-report
     */
    downloadMcxReport: builder.query<DownloadCenterResponse, McxReportParams>({
      query: (params) => ({ url: 'mcx-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterMCX', true),
    }),

    /**
     * Download Equity report
     * @endpoint GET /equity-report
     */
    downloadEquityReport: builder.query<DownloadCenterResponse, EquityReportParams>({
      query: (params) => ({ url: 'equity-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterEquity', true),
    }),

    /**
     * Download Equity Portfolio report
     * @endpoint GET /portfolio
     */
    downloadEquityPortfolioReport: builder.query<DownloadCenterResponse, EquityReportParams>({
      query: (params) => ({
        url: 'portfolio',
        params: { ...(params || {}), cocd: '1', isView: false },
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterEquity', true),
    }),

    /**
     * Download Derivative Portfolio report
     * @endpoint GET /portfolio
     */
    downloadDerivativePortfolioReport: builder.query<DownloadCenterResponse, EquityReportParams>({
      query: (params) => ({
        url: 'portfolio',
        params: { ...(params || {}), cocd: '5', isView: false },
      }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterEquity', true),
    }),

    /**
     * Download Mutual Funds report
     * @endpoint GET /mutual-funds-report
     */
    downloadMutualFundsReport: builder.query<DownloadCenterResponse, MutualFundsReportParams>({
      query: (params) => ({ url: 'mutual-funds-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterMutualFunds', true),
    }),

    /**
     * Download SLBM report
     * @endpoint GET /slbm-report
     */
    downloadSlbmReport: builder.query<DownloadCenterResponse, SlbmReportParams>({
      query: (params) => ({ url: 'slbm-report', params }),
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterSLBM', true),
    }),

    /**
     * Download Client Master PDF (auto-trigger on client selection)
     * @endpoint GET /v2/client-master/download-client-master
     * Note: Returns raw PDF Blob
     */
    downloadClientMaster: builder.query<ClientMasterResponse, ClientMasterParams>({
      query: (params) => {
        const { boId, ...restParams } = params;

        return {
          url: `${clientMasterReports}/v2/client-master/download-client-master`,
          params: {
            ...restParams,
            boId,
          },
          responseHandler: (response) => response.blob(),
        };
      },
      keepUnusedDataFor: 0,
      transformErrorResponse: createApiErrorTransformer('DownloadCenterClientMaster', true),
    }),
  }),
});

// ================================================================
// EXPORTED HOOKS
// ================================================================
//
// Auto-generated hooks for use in React components
// Naming convention: use[EndpointName][Query|Mutation]
// ================================================================

export const {
  useGetCompanyCodesQuery,
  useLazyDownloadHoldingsReportCenterQuery,
  useLazyDownloadLedgerReportForCenterQuery,
  useLazyDownloadPositionReportForCenterQuery,
  useLazyDownloadContractBillReportQuery,
  useLazyDownloadGlobalSummaryForCenterQuery,
  useLazyDownloadIncomeTaxReportQuery,
  useLazyDownloadFnoReportQuery,
  useLazyDownloadPnlSummaryReportQuery,
  useLazyDownloadGlobalDetailsReportQuery,
  useLazyDownloadPayinPayoutReportQuery,
  useLazyDownloadCurrencyDerivativesReportQuery,
  useLazyDownloadMcxReportQuery,
  useLazyDownloadEquityReportQuery,
  useLazyDownloadMutualFundsReportQuery,
  useLazyDownloadSlbmReportQuery,
  useLazyDownloadEquityPortfolioReportQuery,
  useLazyDownloadDerivativePortfolioReportQuery,
  useLazyDownloadClientMasterQuery,
} = downloadCenterApi;

/**
 * ================================================================
 * END OF SERVICE: Download Center Service
 * ================================================================
 *
 * @exports          downloadCenterApi, hooks for React components
 * @dependencies     RTK Query, TypeScript types from @/types/downloads
 * @usedBy           Download center components and pages
 *
 * @maintenance
 * - Cache strategy: Review TTL settings quarterly
 * - Error handling: Monitor error rates and adjust retry logic
 * - Performance: Track query performance and optimize slow endpoints
 *
 * @separation
 * - Extracted from reportsService.ts for better code organization
 * - Maintains all original functionality with improved modularity
 * - Uses dedicated types from @/types/downloads for type safety
 *
 * ================================================================
 */
